import React, { useEffect, useState, Fragment, useMemo, useCallback } from 'react';
import axios from 'axios';
import Chart from "../components/Chart";
import SearchBarPublished from "../components/SearchBarPublished";
import Table from "../components/Table";
import { API_BASE_URL } from "../config";
import Loading from "../components/Loading";

const SECURITY_ORDER = { critical: 0, warning: 1, ok: 2 };

const SitesPublies = () => {
    const [prod, setProd] = useState(null);
    const [published, setPublished] = useState(null);
    const [sortBy, setSortBy] = useState({ column: null, ascending: true });
    const [loading, setLoading] = useState(false);
    const [securityReports, setSecurityReports] = useState({});

    const columnMapping = {
        "Publié le": "date",
        'Site': "url",
        "Admin URL": "url",
        "Version XGrid": "version",
        "Serveur": "server",
        "Secteur": "branch_names",
        "Console": "sended",
        "Client": "firm",
        "Client actif": "is_active",
        "Statut": "is_down",
        "Sécurité": "security",
    };

    useEffect(() => {
        document.title = 'Sites publiés'
        fetchPublishedData();
        fetchSecurityReports();
    }, []);

    const fetchSecurityReports = () => {
        axios.get(`${API_BASE_URL}/get_security_report`)
            .then(res => {
                if (res.data.status === 'ok' && Array.isArray(res.data.data)) {
                    const mapped = res.data.data.reduce((acc, row) => {
                        let parsedDetails = {};
                        try {
                            parsedDetails = JSON.parse(row.details || '{}');
                        } catch (e) {
                            console.error('Error parsing security details for', row.website, e);
                        }
                        acc[row.website] = { ...row, parsedDetails };
                        return acc;
                    }, {});
                    setSecurityReports(mapped);
                }
            })
            .catch(err => console.error('Error fetching security reports:', err));
    };

    const handleRunSecurityScan = () => {
        if (!window.confirm("Voulez-vous lancer un scan de sécurité sur tous les sites actifs ? Cela peut prendre quelques minutes.")) return;

        setLoading(true);
        axios.post(`${API_BASE_URL}/run_security_scan`)
            .then(() => {
                alert("Le scan a été lancé en arrière-plan. Les résultats apparaîtront au fur et à mesure.");
                // Refetch after a short delay to see if first results are in
                setTimeout(fetchSecurityReports, 5000);
            })
            .catch(err => console.error('Error starting security scan:', err))
            .finally(() => setLoading(false));
    };

    const handleRunMonitoringScan = () => {
        if (!window.confirm("Voulez-vous lancer un scan de monitoring sur tous les sites actifs ? Cela peut prendre quelques minutes.")) return;

        setLoading(true);
        axios.post(`${API_BASE_URL}/run_monitoring_scan`)
            .then(() => {
                alert("Le scan de monitoring a été lancé en arrière-plan.");
            })
            .catch(err => console.error('Error starting monitoring scan:', err))
            .finally(() => setLoading(false));
    };

    const fetchPublishedData = () => {
        axios.post(`${API_BASE_URL}/get_published`)
            .then(response => {

                console.log(response)
                // 20261001 : remove 'solution-digitale.ch', to allow the system to get our website
                // !item.url.includes('.solution-digitale') &&
                const filteredData = response.data.filter(item =>
                    !item.url.includes('xgrid') &&
                    !item.url.includes('00_') &&
                    !item.url.includes('agw-serrures.ch') &&
                    !item.url.includes('01')
                );

                const sortedData = filteredData.sort((a, b) => new Date(b.date) - new Date(a.date));

                const formattedData = sortedData.map(item => ({
                    ...item,
                    date: new Date(item.date).toLocaleDateString('ch-FR'),
                    firm: item.firm
                }));

                setProd(formattedData);
                setPublished(formattedData);


            })
            .catch(error => {
                console.error('Error fetching data:', error);
            });
    };

    const handleSort = useCallback((columnName) => {
        if (!columnName) { setSortBy({ column: null, ascending: true }); return; }
        setSortBy(prevSortBy => {
            const actualColumn = columnMapping[columnName] || columnName;
            const ascending = actualColumn === prevSortBy.column ? !prevSortBy.ascending : true;
            return { column: actualColumn, ascending };
        });
    }, [columnMapping]);

    const updateWebsites = () => {
        setLoading(true);
        axios.post(`${API_BASE_URL}/update_published`)
            .then(response => {
                // Optionally handle response here
            })
            .catch(error => {
                console.error('Error updating published sites:', error);
            })
            .finally(() => {
                setLoading(false);
                // Refetch the published data after the update is done
                fetchPublishedData();
            });
    };

    const publishedArray = useMemo(() => Array.isArray(published) ? published : [], [published]);

    const sortedArray = useMemo(() => {
        return [...publishedArray].sort((a, b) => {
            if (sortBy.column === 'is_down') {
                const da = a.is_down ?? null;
                const db = b.is_down ?? null;
                if (da === null && db !== null) return 1;
                if (db === null && da !== null) return -1;
                return sortBy.ascending ? da - db : db - da;
            } else if (sortBy.column === 'security') {
                const sa = securityReports[a.url]?.status ? (SECURITY_ORDER[securityReports[a.url].status] ?? 99) : 99;
                const sb = securityReports[b.url]?.status ? (SECURITY_ORDER[securityReports[b.url].status] ?? 99) : 99;
                if (sa === 99 && sb !== 99) return 1;
                if (sb === 99 && sa !== 99) return -1;
                return sortBy.ascending ? sa - sb : sb - sa;
            } else if (sortBy.column === 'date') {
                const parseDate = (str) => {
                    if (!str) return 0;
                    const [day, month, year] = str.split('/');
                    return new Date(`${year}-${month}-${day}`).getTime();
                };
                const dateA = parseDate(a.date);
                const dateB = parseDate(b.date);
                return sortBy.ascending ? dateA - dateB : dateB - dateA;
            } else if (sortBy.column === 'version') {
                const versionA = a.version === null ? '0' : a.version;
                const versionB = b.version === null ? '0' : b.version;
                return sortBy.ascending ? versionA.localeCompare(versionB) : versionB.localeCompare(versionA);
            } else if (sortBy.column === 'url') {
                return sortBy.ascending ? a.url.localeCompare(b.url) : b.url.localeCompare(a.url);
            } else if (sortBy.column === 'firm') {
                return sortBy.ascending ? (a.firm || '').localeCompare(b.firm || '') : (b.firm || '').localeCompare(a.firm || '');
            } else if (sortBy.column === 'server') {
                return sortBy.ascending ? (a.server || '').localeCompare(b.server || '') : (b.server || '').localeCompare(a.server || '');
            } else if (sortBy.column === 'branch_names') {
                return sortBy.ascending
                    ? (a.branch_names ?? '').localeCompare(b.branch_names ?? '')
                    : (b.branch_names ?? '').localeCompare(a.branch_names ?? '');
            } else if (sortBy.column === 'sended') {
                return sortBy.ascending ? a.sended - b.sended : b.sended - a.sended;
            } else if (sortBy.column === 'is_active') {
                return sortBy.ascending ? a.is_active - b.is_active : b.is_active - a.is_active;
            }
            return 0;
        });
    }, [publishedArray, sortBy.column, sortBy.ascending, securityReports, SECURITY_ORDER]);


    return (
        <Fragment>
            <div>
                <h2>Liste des sites publiés</h2>
                <Chart data={prod} type={'area-spline'} dateField={'date'} />
                <div>
                    <SearchBarPublished
                        totalPositions={prod}
                        setPublished={setPublished}
                        totalPositionsLength={publishedArray}
                        searchLabel={'sites trouvés'}
                        loading={loading}
                        updateWebsites={updateWebsites}
                        handleRunMonitoringScan={handleRunMonitoringScan}
                        handleRunSecurityScan={handleRunSecurityScan}
                    />

                    {/* Quick-sort buttons */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '10px 0', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>Trier par :</span>
                        {[
                            { label: 'Statut', column: 'is_down', icon: 'fa-circle', activeColor: '#ef4444' },
                            { label: 'Sécurité', column: 'security', icon: 'fa-shield-halved', activeColor: '#ef4444' },
                        ].map(({ label, column, icon, activeColor }) => {
                            const isActive = sortBy.column === column;
                            return (
                                <button
                                    key={column}
                                    onClick={() => handleSort(label)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        padding: '5px 12px',
                                        background: isActive ? '#1e293b' : '#f1f5f9',
                                        color: isActive ? '#fff' : '#475569',
                                        border: `1.5px solid ${isActive ? '#1e293b' : '#e2e8f0'}`,
                                        borderRadius: '20px',
                                        fontSize: '12px', fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    <i className={`fa-solid ${icon}`} style={{ fontSize: '11px', color: isActive ? '#fff' : activeColor }} />
                                    {label}
                                    {isActive && <span style={{ opacity: 0.8 }}>{sortBy.ascending ? '▲' : '▼'}</span>}
                                </button>
                            );
                        })}
                        {(sortBy.column === 'is_down' || sortBy.column === 'security') && (
                            <button
                                onClick={() => handleSort('')}
                                style={{
                                    padding: '5px 10px', background: 'none', border: 'none',
                                    color: '#94a3b8', fontSize: '12px', cursor: 'pointer'
                                }}
                            >
                                ✕ Réinitialiser
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <Loading />
                    ) : (
                        <Table
                            typeOfPosition={"finished"}
                            data={sortedArray}
                            handleSort={handleSort}
                            sortBy={sortBy}
                            hidePagination={false}
                            maxEntries={100}
                            headersData={['date', 'url', 'lang', 'firm', 'branch_names', 'url_admin', 'version', 'server', 'sended', 'is_active']}
                            headers={["Publié le", "Site", '', 'Client', 'Secteur', "Admin URL", "Version XGrid", "Serveur", "Console", "Client actif"]}
                            securityReports={securityReports}
                        />
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default SitesPublies;
